import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface PanelOption {
  id: string;
  brand: string;
  model: string;
  wattage: number;
  efficiency: number;
  price: number;
  image?: string;
}

export interface InverterOption {
  id: string;
  brand: string;
  model: string;
  power: number;
  hybrid: boolean;
  price: number;
  image?: string;
}

export interface RoofType {
  id: string;
  name: string;
  description: string;
  image?: string;
}

export interface MountingSystem {
  id: string;
  name: string;
  roofTypes: string[];
  price: number;
  description: string;
}

export interface InstallationMethod {
  id: string;
  name: string;
  description: string;
  rows: number;
  orientation: 'portrait' | 'landscape';
}

export interface Configuration {
  panel?: PanelOption;
  inverter?: InverterOption;
  roofType?: RoofType;
  mountingSystem?: MountingSystem;
  installationMethod?: InstallationMethod;
  panelCount: number;
}

interface ConfiguratorContextType {
  configuration: Configuration;
  setConfiguration: (config: Partial<Configuration>) => void;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  resetConfiguration: () => void;
}

const ConfiguratorContext = createContext<ConfiguratorContextType | undefined>(undefined);

const initialConfiguration: Configuration = {
  panelCount: 12
};

export const ConfiguratorProvider = ({ children }: { children: ReactNode }) => {
  const [configuration, setConfigurationState] = useState<Configuration>(initialConfiguration);
  const [currentStep, setCurrentStep] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const setConfiguration = (config: Partial<Configuration>) => {
    setConfigurationState(prev => ({ ...prev, ...config }));
  };

  const resetConfiguration = () => {
    setConfigurationState(initialConfiguration);
    setCurrentStep(0);
  };

  return (
    <ConfiguratorContext.Provider value={{
      configuration,
      setConfiguration,
      currentStep,
      setCurrentStep,
      isOpen,
      setIsOpen,
      resetConfiguration
    }}>
      {children}
    </ConfiguratorContext.Provider>
  );
};

export const useConfigurator = () => {
  const context = useContext(ConfiguratorContext);
  if (!context) {
    throw new Error('useConfigurator must be used within ConfiguratorProvider');
  }
  return context;
};